// ChunkSet, ported from internal/wire/tick.go.
//
// A bitmap over the board's chunks. The tick's dirty set and a client's
// subscription set are the same shape, which is what makes "expected DELTAs" a
// one-line intersection on both ends of the wire.
//
// Two details SPEC §3 says a port will get wrong if they are not written down,
// and both are pinned by tests here:
//
//   - the bitmap length is the CEILING of NumChunks/8 — at 100 chunks that is 13
//     bytes, not 12.5, and flooring it silently drops the last four chunks;
//   - the spare bits above the last chunk must be zero on both sides, because a
//     bit up there names a chunk that is not on the board.

import { NumChunks, validChunk, type ChunkID } from './core';
import { errChunkID } from './errors';

/** The bitmap width, derived from the board rather than fixed. */
export const ChunkSetBytes = Math.ceil(NumChunks / 8);

/**
 * A set of chunk ids as a bitmap.
 *
 * Bit c lives in byte c>>3 at 1<<(c&7) — least significant bit first, which is
 * the order the Go encoder writes and the order this reads.
 */
export class ChunkSet {
  readonly bits: Uint8Array;

  constructor(bits?: Uint8Array) {
    if (bits && bits.byteLength !== ChunkSetBytes) {
      throw new RangeError(`wire: a chunk set is ${ChunkSetBytes} bytes, got ${bits.byteLength}`);
    }
    this.bits = bits ? bits.slice() : new Uint8Array(ChunkSetBytes);
  }

  static from(...ids: ChunkID[]): ChunkSet {
    const s = new ChunkSet();
    for (const c of ids) s.set(c);
    return s;
  }

  /** Every chunk on the board, which is what a whole-board subscriber holds. */
  static full(): ChunkSet {
    const s = new ChunkSet();
    for (let c = 0; c < NumChunks; c++) s.bits[c >> 3]! |= 1 << (c & 7);
    return s;
  }

  clone(): ChunkSet {
    return new ChunkSet(this.bits);
  }

  set(c: ChunkID): this {
    if (!validChunk(c)) throw errChunkID();
    this.bits[c >> 3]! |= 1 << (c & 7);
    return this;
  }

  unset(c: ChunkID): this {
    if (!validChunk(c)) throw errChunkID();
    this.bits[c >> 3]! &= ~(1 << (c & 7));
    return this;
  }

  /** An out-of-range chunk is never a member. */
  has(c: ChunkID): boolean {
    if (!validChunk(c)) return false;
    return (this.bits[c >> 3]! & (1 << (c & 7))) !== 0;
  }

  get count(): number {
    let n = 0;
    for (const b of this.bits) n += POPCOUNT[b]!;
    return n;
  }

  get empty(): boolean {
    return this.count === 0;
  }

  /** The set in ascending chunk order. */
  chunks(): ChunkID[] {
    const out: ChunkID[] = [];
    for (let c = 0; c < NumChunks; c++) {
      if ((this.bits[c >> 3]! & (1 << (c & 7))) !== 0) out.push(c);
    }
    return out;
  }

  /** Dirty ∩ subscribed is exactly the DELTAs a client should expect this tick. */
  intersect(o: ChunkSet): ChunkSet {
    const out = new ChunkSet();
    for (let i = 0; i < ChunkSetBytes; i++) out.bits[i] = this.bits[i]! & o.bits[i]!;
    return out;
  }

  /** Folds a pan diff in, so the set completeness is judged against stays in step. */
  apply(d: { add?: readonly ChunkID[]; remove?: readonly ChunkID[] }): this {
    for (const c of d.remove ?? []) this.unset(c);
    for (const c of d.add ?? []) this.set(c);
    return this;
  }

  /**
   * Whether the padding bits past NumChunks are clear. They must be: a set bit up
   * there would name a chunk that is not on the board.
   */
  canonical(): boolean {
    const spare = ChunkSetBytes * 8 - NumChunks;
    if (spare === 0) return true;
    return this.bits[ChunkSetBytes - 1]! >>> (8 - spare) === 0;
  }

  equals(o: ChunkSet): boolean {
    for (let i = 0; i < ChunkSetBytes; i++) if (this.bits[i] !== o.bits[i]) return false;
    return true;
  }
}

const POPCOUNT = new Uint8Array(256);
for (let i = 0; i < 256; i++) {
  POPCOUNT[i] = (i & 1) + POPCOUNT[i >> 1]!;
}
